import API from "./api";
import * as Sentry from "@sentry/react";

/**
 * Service wrappers for accessing various APIs
 */

/**
 * Configures and returns an XHR GET function which returns the entity by ID
 *
 * @param {string} entityName - The name of the entity (a.k.a. "kind")
 * @returns {object} entity - An entity API object which contains an accessor function
 * @returns {function} entity.get A function which can be used to get an entity by ID
 */
function Entity(entityName: string) {
  entityName = entityName.toLowerCase();
  return {
    get: async (id: string | Number) => {
      try {
        const resp = await API.get(`/api/entity?${entityName}=${id}`);
        return resp.data;
      } catch (error) {
        Sentry.captureException(error);
        return null;
      }
    },
    getParties: async (ids: string[], type: string) => {
      try {
        const _ids = ids.map((id) => `id=${id}`).join("&");
        const resp = await API.get(`/api/entity?type=${type}&${_ids}`);
        return resp.data;
      } catch (error) {
        Sentry.captureException(error);
        return null;
      }
    },
  };
}

export const Court = Entity("court");
export const Judge = Entity("judge");
export const Attorney = Entity("attorney");
export const LawFirm = Entity("law_firm");
export const Party = Entity("party");

export const MaxNumberResults = 5000;
export const Search = {
  async get(query: string, type: string, limit?: number, signal?: AbortSignal) {
    limit = limit ? limit : MaxNumberResults;
    signal = signal ? signal : undefined;
    const _query = encodeURIComponent(query);
    return API.get(`/api/results?type=${type}&q=${_query}&n=${limit}`, {
      signal: signal,
    }).then((resp) => resp.data);
  },
  async getEntitiesByIds(ids: string[], type: string) {
    const _ids = ids.map((id) => `id=${id}`).join("&");
    return API.get(`/api/results?type=${type}&${_ids}`).then(
      (resp) => resp.data,
    );
  },
};

export const Dockets = {
  async get(
    docketsContext: string,
    id: string,
    query: string,
    entityType: string,
  ) {
    return await API.get(
      `/api/${docketsContext}?${entityType}=${id}&${query}&n=${MaxNumberResults}`,
    ).then((resp) => resp);
  },
};

export const Suggest = {
  async get(entityType: string, query: string) {
    return await API.get(`/api/suggest/${entityType}?q=${query}`).then(
      (resp) => resp,
    );
  },
  getUrlBase(entityType: string): Function {
    return (query: string) => `/api/suggest/${entityType}?q=${query}`;
  },
};

export const UploadHtml = {
  async post(html: string) {
    return API.post("/api/export/upload-html", html, {
      headers: {
        "content-type": "text/html",
      },
    }).then((resp) => resp.data);
  },
};

export const DownloadPdf = {
  async get(export_id: string) {
    return API.get(`/api/export/pdf/${export_id}`, {
      responseType: "blob",
    }).then((response) => {
      const blob = new Blob([response.data]);
      const fileName = "export.pdf";
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    });
  },
};

interface CourtCoverageResponse {
  normal: Array<number>;
  enhanced: Array<number>;
}

export const getCourtCoverage = (): Promise<CourtCoverageResponse> => {
  return API.get(`/api/court-coverage`, {
    responseType: "json",
  }).then((response) => {
    return response.data;
  });
};
